import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/auth.context';
import { ThemeToggle } from './ThemeToggle';
import { ErrorBoundary } from './ErrorBoundary';
import { brand, type Instancia } from '../api/client';

/** Alterna entre as instâncias BR (português), EN (inglês) e ES (espanhol).
 * Cada botão extra só aparece se a URL da instância (VITE_EN_API_URL /
 * VITE_ES_API_URL) estiver configurada no build. O login é separado por
 * instância, então trocar não desloga as outras. */
function BrandSwitcher(): ReactNode {
  if (!brand.enAvailable() && !brand.esAvailable()) return null;
  const current = brand.current();
  const opcoes: { id: Instancia; rotulo: string; visivel: boolean }[] = [
    { id: 'br', rotulo: '🇧🇷 PT', visivel: true },
    { id: 'en', rotulo: '🇺🇸 EN', visivel: brand.enAvailable() },
    { id: 'es', rotulo: '🇲🇽 ES', visivel: brand.esAvailable() },
  ];
  return (
    <div className="flex items-center gap-1 text-xs" title="Instância (idioma)">
      {opcoes
        .filter((o) => o.visivel)
        .map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => brand.set(o.id)}
            aria-pressed={current === o.id}
            className={`px-2 py-1 rounded ${current === o.id ? 'bg-sara-accent text-sara-accent-fg' : 'bg-sara-surface text-sara-text-muted'}`}
          >
            {o.rotulo}
          </button>
        ))}
    </div>
  );
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

interface NavGroup {
  title: string | null;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: null,
    items: [{ label: 'Dashboard', path: '/', icon: '◐' }],
  },
  {
    title: 'Atendimento',
    items: [
      { label: 'Conversas', path: '/conversas', icon: '✦' },
      { label: 'Chats', path: '/chats', icon: '💬' },
      { label: 'Tickets', path: '/tickets', icon: '◇' },
      { label: 'Mapas', path: '/mapas', icon: '☉' },
    ],
  },
  {
    title: 'Clientes',
    items: [
      { label: 'Clientes', path: '/clientes', icon: '◯' },
      { label: 'Duplicatas', path: '/duplicatas', icon: '⧉' },
      { label: 'Compras', path: '/compras', icon: '$' },
    ],
  },
  {
    title: 'Sara (qualidade)',
    items: [
      { label: 'Insights', path: '/insights', icon: '✶' },
      { label: 'Auditoria', path: '/auditoria', icon: '⚖' },
      // 'Auditoria IA' 🛡 saiu em 05/08/2026 (ADR 0005): a auditoria por IA está
      // parqueada. A 'Auditoria' ⚖ acima é outra coisa (consulta SQL, sem LLM).
    ],
  },
  {
    title: 'Ferramentas',
    items: [
      { label: 'Disparos', path: '/whatsapp/disparos', icon: '📣' },
      { label: 'Nutrição', path: '/nutricao', icon: '✉️' },
      { label: 'Ativação', path: '/ativacao', icon: '🌱' },
      { label: 'Canais Cloud', path: '/whatsapp/canais', icon: '☁' },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { label: 'Webhooks', path: '/webhooks', icon: '⌁' },
      { label: 'Emails', path: '/emails', icon: '✉' },
      { label: 'Logs', path: '/system-logs', icon: '☰' },
      { label: 'Avisos', path: '/system-logs?source=alerta', icon: '🔔' },
      { label: 'Modo Teste', path: '/test-mode', icon: '⚑' },
      { label: 'Playground', path: '/playground', icon: '◊' },
      { label: 'Configurações', path: '/config', icon: '⚙' },
    ],
  },
];

export function AppLayout({ children }: { children: ReactNode }): JSX.Element {
  // A rota entra como chave do boundary: trocar de tela limpa um erro antigo,
  // então uma tela quebrada não carimba a próxima.
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen flex text-sara-text">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 btn-primary z-50"
      >
        Pular pro conteúdo
      </a>

      {/* Sidebar */}
      <aside
        className="w-64 bg-sara-surface/60 backdrop-blur-xl border-r border-sara-border flex flex-col"
        aria-label="Navegação lateral"
      >
        <div className="px-5 py-6 border-b border-sara-border">
          <Link to="/" className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-sara-accent to-sara-accent-soft text-sara-accent-fg text-base font-bold shadow-sara-pop"
            >
              ☾
            </span>
            <span className="brand-wordmark text-xl font-semibold tracking-wide">Sara</span>
            <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-sara-text-subtle self-end mb-1">
              Tarot
            </span>
          </Link>
        </div>

        <nav
          className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto"
          aria-label="Navegação principal"
        >
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.title ?? 'top'} className={gi > 0 ? 'pt-3' : ''}>
              {group.title && (
                <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-sara-text-subtle">
                  {group.title}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      [
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium',
                        'transition-all duration-150',
                        isActive
                          ? 'bg-sara-accent/10 text-sara-accent'
                          : 'text-sara-text-muted hover:bg-sara-surface-hover hover:text-sara-text',
                      ].join(' ')
                    }
                  >
                    <span aria-hidden="true" className="w-4 text-center text-sara-text-subtle">
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-4 py-4 border-t border-sara-border space-y-3">
          <div className="text-sm">
            <p className="text-sara-text font-medium truncate" title={user?.email}>
              {user?.name}
            </p>
            <p className="text-sara-text-subtle text-xs truncate">{user?.email}</p>
          </div>
          <button type="button" onClick={() => void logout()} className="btn-ghost w-full text-xs">
            Sair
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 border-b border-sara-border bg-sara-bg/70 backdrop-blur sticky top-0 z-10 flex items-center justify-end px-6 gap-3">
          <BrandSwitcher />
          <ThemeToggle />
        </header>

        <main id="main" className="flex-1 p-6 overflow-auto animate-fade-in">
          {/* O boundary embrulha só o miolo: se uma tela quebra, o menu
              continua de pé e dá pra ir pra outra sem recarregar. */}
          <ErrorBoundary resetKey={location.pathname + location.search}>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
